import React, { useEffect, useState } from 'react';
import { Modal, Input } from 'semantic-ui-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import CheckBox from '../ui/CheckBox';
import Button from '../ui/Button';
import colors from '../../css/colors';

interface ClientAbstractEmailRecipient {
  id: number;
  clientAbstractDeliveryConfigId: number;
  emailAddress: string;
}

interface ClientAbstractDeliveryConfig {
  id: number;
  clientAbstractRuleId: number;
  isContin: boolean;
  useIntegration: boolean;
  useEmail: boolean;
  sendToRequester: boolean;
  sendToDefaults: boolean;
  sendBillingDoc: boolean;
  clientAbstractEmailRecipients: ClientAbstractEmailRecipient[];
}

interface ClientAbstractRule {
  id: number;
  clientId: number;
  titleNumberPrefix: string | null;
  sendSearchWithFee: boolean;
  sendOnHoliday: boolean;
  notes: string | null;
  createdAt?: string;
  updatedAt?: string;
  client?: unknown;
  clientAbstractDeliveryConfigs: ClientAbstractDeliveryConfig[];
}

interface AbstractRuleModalProps {
  clientId: number;
  clientName: string;
  closeModal: () => void;
  onSubmit: () => void;
}

const buildDefaultRule = (clientId: number): ClientAbstractRule => ({
  id: 0,
  clientId,
  titleNumberPrefix: '',
  sendSearchWithFee: false,
  sendOnHoliday: false,
  notes: '',
  clientAbstractDeliveryConfigs: [
    {
      id: 0,
      clientAbstractRuleId: 0,
      isContin: false,
      useIntegration: false,
      useEmail: true,
      sendToRequester: false,
      sendToDefaults: false,
      sendBillingDoc: false,
      clientAbstractEmailRecipients: [],
    },
    {
      id: 0,
      clientAbstractRuleId: 0,
      isContin: true,
      useIntegration: false,
      useEmail: true,
      sendToRequester: false,
      sendToDefaults: false,
      sendBillingDoc: false,
      clientAbstractEmailRecipients: [],
    },
  ],
});

interface DeliveryConfigPanelProps {
  config: ClientAbstractDeliveryConfig;
  onChange: (updated: ClientAbstractDeliveryConfig) => void;
}

function DeliveryConfigPanel({ config, onChange }: DeliveryConfigPanelProps) {
  const [emailInput, setEmailInput] = useState('');

  const update = (patch: Partial<ClientAbstractDeliveryConfig>) => onChange({ ...config, ...patch });

  const addEmail = () => {
    const trimmed = emailInput.trim();
    if (!trimmed) return;
    update({
      clientAbstractEmailRecipients: [
        ...config.clientAbstractEmailRecipients,
        { id: 0, clientAbstractDeliveryConfigId: config.id, emailAddress: trimmed },
      ],
    });
    setEmailInput('');
  };

  const removeEmail = (index: number) => {
    update({
      clientAbstractEmailRecipients: config.clientAbstractEmailRecipients.filter((_, i) => i !== index),
    });
  };

  return (
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: colors.blackText }}>
        {config.isContin ? 'Continuation' : 'Standard'}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <CheckBox
          label="Use Integration"
          checked={config.useIntegration}
          onCheckChanged={(val: boolean) => update({ useIntegration: val })}
        />
        <CheckBox
          label="Use Email"
          checked={config.useEmail}
          onCheckChanged={(val: boolean) => update({ useEmail: val })}
        />
        <CheckBox
          label="Send to Requester"
          checked={config.sendToRequester}
          onCheckChanged={(val: boolean) => update({ sendToRequester: val })}
        />
        <CheckBox
          label="Send to Defaults"
          checked={config.sendToDefaults}
          onCheckChanged={(val: boolean) => update({ sendToDefaults: val })}
        />
        <CheckBox
          label="Send Billing Doc"
          checked={config.sendBillingDoc}
          onCheckChanged={(val: boolean) => update({ sendBillingDoc: val })}
        />
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 13, marginBottom: 6 }}>Email Recipients</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <Input
            placeholder="Add email"
            value={emailInput}
            onChange={(_, { value }) => setEmailInput(value)}
            onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && addEmail()}
            style={{ height: 36 }}
          />
          <Button secondary onClick={addEmail} type="button" overRidestyle={{ height: 36, minWidth: 60 }}>
            Add
          </Button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          {config.clientAbstractEmailRecipients.map((r, i) => (
            <span
              key={i}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                backgroundColor: colors.greyOrder,
                borderRadius: 4,
                padding: '2px 8px',
                marginRight: 6,
                marginBottom: 6,
                fontSize: 13,
              }}
            >
              {r.emailAddress}
              <span
                style={{ marginLeft: 6, cursor: 'pointer', fontWeight: 700 }}
                onClick={() => removeEmail(i)}
              >
                ×
              </span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AbstractRuleModal({
  clientId,
  clientName,
  closeModal,
  onSubmit,
}: AbstractRuleModalProps) {
  const [rule, setRule] = useState<ClientAbstractRule>(buildDefaultRule(clientId));
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get<ClientAbstractRule>(`/api/abstractRule/GetClientAbstractRules/${clientId}`)
      .then(({ data }) => {
        if (cancelled) return;
        const fetched = data ?? buildDefaultRule(clientId);
        const hasRegular = fetched.clientAbstractDeliveryConfigs.some(c => !c.isContin);
        const hasContin = fetched.clientAbstractDeliveryConfigs.some(c => c.isContin);
        const configs = [...fetched.clientAbstractDeliveryConfigs];
        if (!hasRegular) {
          configs.unshift({
            id: 0,
            clientAbstractRuleId: fetched.id,
            isContin: false,
            useIntegration: false,
            useEmail: false,
            sendToRequester: false,
            sendToDefaults: false,
            sendBillingDoc: false,
            clientAbstractEmailRecipients: [],
          });
        }
        if (!hasContin) {
          configs.push({
            id: 0,
            clientAbstractRuleId: fetched.id,
            isContin: true,
            useIntegration: false,
            useEmail: false,
            sendToRequester: false,
            sendToDefaults: false,
            sendBillingDoc: false,
            clientAbstractEmailRecipients: [],
          });
        }
        setRule({ ...fetched, clientAbstractDeliveryConfigs: configs });
      })
      .catch(() => {
        if (!cancelled) setRule(buildDefaultRule(clientId));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const updateConfig = (isContin: boolean, updated: ClientAbstractDeliveryConfig) => {
    setRule(prev => ({
      ...prev,
      clientAbstractDeliveryConfigs: prev.clientAbstractDeliveryConfigs.map(c =>
        c.isContin === isContin ? updated : c
      ),
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post('/api/abstractRule/UploadAbstractRule', rule);
      toast('Rules saved successfully', {
        hideProgressBar: true,
        position: 'top-center',
        type: 'success',
        autoClose: 2000,
      });
      onSubmit();
      closeModal();
    } catch {
      toast('Failed to save rules', {
        hideProgressBar: true,
        position: 'top-center',
        type: 'error',
        autoClose: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const standardConfig = rule.clientAbstractDeliveryConfigs.find(c => !c.isContin);
  const continConfig = rule.clientAbstractDeliveryConfigs.find(c => c.isContin);

  return (
    <Modal open onClose={closeModal} style={{ maxWidth: 800, width: '100%' }}>
      <Modal.Header>{clientName} Abstract Rules</Modal.Header>

      <Modal.Content style={{ overflow: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: colors.greyDark }}>Loading...</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={{ marginBottom: 6, fontSize: 13 }}>Prefix</div>
                <Input
                  value={rule.titleNumberPrefix ?? ''}
                  onChange={(_, { value }) =>
                    setRule(prev => ({ ...prev, titleNumberPrefix: value || null }))
                  }
                  style={{ height: 36 }}
                />
              </div>
              <div style={{ flex: 2, minWidth: 200 }}>
                <div style={{ marginBottom: 8, fontSize: 13 }}>Notes</div>
                <textarea
                  value={rule?.notes ?? ''}
                  onChange={e => {
                    const val = e.target.value;
                    setRule(prev => ({ ...prev, notes: val || null }));
                  }}
                  rows={3}
                  style={{
                    width: '100%',
                    border: `1px solid ${colors.grey}`,
                    borderRadius: 5,
                    padding: '8px 10px',
                    fontSize: 14,
                    resize: 'vertical',
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
              <CheckBox
                label="Send Search With Fee"
                checked={rule.sendSearchWithFee}
                onCheckChanged={(val: boolean) => setRule(prev => ({ ...prev, sendSearchWithFee: val }))}
              />
              <CheckBox
                label="Send on Holiday"
                checked={rule.sendOnHoliday}
                onCheckChanged={(val: boolean) => setRule(prev => ({ ...prev, sendOnHoliday: val }))}
              />
            </div>

            <div style={{ borderTop: `1px solid ${colors.greyBorder}`, margin: '16px 0' }} />

            <div style={{ display: 'flex', gap: 24 }}>
              {standardConfig && (
                <DeliveryConfigPanel
                  config={standardConfig}
                  onChange={updated => updateConfig(false, updated)}
                />
              )}
              <div style={{ borderLeft: `1px solid ${colors.greyBorder}` }} />
              {continConfig && (
                <DeliveryConfigPanel
                  config={continConfig}
                  onChange={updated => updateConfig(true, updated)}
                />
              )}
            </div>
          </>
        )}
      </Modal.Content>

      <Modal.Actions>
        <Button secondary onClick={closeModal} disabled={submitting} type="button">
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={submitting || loading} loadingStatus={submitting}>
          Submit
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
